import { DateTime, Duration } from "luxon"
import fp from "lodash/fp"

import type { Palette } from "@mui/material"
import type { TFunction } from "i18next"

import { ICE_TYPES_BY_SEVERITY } from "helpers/utils/icing"

import type {
  SingleStatPlotView,
  RecordType,
  SingleStatViewProperties,
} from "types/dashboard.types"
import type { SingleStatConfig } from "types/singleStatPlot.types"

import { valueFormatter } from "helpers/formatters/plots/plots"
import { isValidValue } from "helpers/utils/common"
import { ICE_ACCRETION_SEVERITY_FIELD } from "helpers/config/telemetryFieldDefs"

interface SingleStatConfigParams {
  view: SingleStatPlotView
  data: RecordType[]
  palette: Palette
  t: TFunction
  locale?: string
}

export default function singleStatConfig({
  view,
  data,
  palette,
  locale,
  t,
}: SingleStatConfigParams): SingleStatConfig {
  const formattedData = getValue(data, view, t, locale)

  const config = {
    data: formattedData.value instanceof Error ? null : formattedData.value,
    error: formattedData.value instanceof Error ? formattedData.value : null,
    isValid: !(formattedData.value instanceof Error),
    textColor: palette.text.primary,
    footer: formattedData.footer ?? [],
    size: view.properties.size,
  }

  return config
}

const getValue = (
  data: RecordType[],
  view: SingleStatPlotView,
  t: TFunction,
  locale?: string,
) => {
  const name = view.name.toLowerCase()

  const lastResult = data[data.length - 1]
  const firstResult = data[0]

  const lastResultValue = lastResult && (lastResult._value as string | number | undefined)

  const firstResultValue = firstResult && firstResult._value

  if (
    data.length > 0 &&
    (isValidValue(firstResultValue) || isValidValue(lastResultValue))
  ) {
    if (isValidValue(firstResultValue)) {
      // For the 'first message' stat we want the first object (assuming that the data arrives in ascending order).
      if (name === "first message" && typeof firstResultValue === "string") {
        const value = dateFormatter(firstResultValue)
        const footer = getPlotFooter(firstResult, view.properties, t)
        return { value, footer }
      }
    }
    if (isValidValue(lastResultValue)) {
      // Special handling for ice accretion detection - show ice type name as main value
      if (
        name === "last ice accretion detection" &&
        typeof lastResultValue === "number"
      ) {
        const iceType = ICE_TYPES_BY_SEVERITY[lastResultValue]
        const iceTypeName = iceType ? t(iceType.labelKey) : String(lastResultValue)
        const footer = getPlotFooter(lastResult, view.properties, t)
        const color = iceType?.color
        return { value: iceTypeName, footer, color }
      }
      // For the 'last message' stat we want the last object (assuming that the data arrives in ascending order).
      const footer = getPlotFooter(lastResult, view.properties, t)
      if (name === "last message" && typeof lastResultValue === "string") {
        return {
          value: getData(dateFormatter(lastResultValue), view.properties, locale),
          footer,
        }
      } else if (name.includes("time per loop")) {
        return {
          value: getData(timeFormatter(lastResultValue), view.properties, locale),
          footer,
        }
      } else if (typeof lastResultValue === "number") {
        return { value: getData(lastResultValue, view.properties, locale), footer }
      } else {
        return {
          value: getData(lastResultValue as string, view.properties, locale),
          footer,
        }
      }
    }
  }
  return { value: "No data" }
}

const getPlotFooter = (
  data: RecordType,
  viewProperties: SingleStatViewProperties,
  t: TFunction,
): string[] => {
  const footerLines: string[] = []
  viewProperties.footerFields?.forEach((field) => {
    const value = data[field]
    if (!fp.isNil(value)) {
      if (field === "_time" && typeof value === "string") {
        const formattedDate = DateTime.fromISO(value).toFormat("yyyy-MM-dd HH:mm:ss")
        footerLines.push(formattedDate)
      } else if (field === "_value" && data._field === ICE_ACCRETION_SEVERITY_FIELD) {
        // Special handling for icing severity - add label
        footerLines.push(`${t("plots_tooltip.SEVERITY")}: ${value}`)
      } else {
        footerLines.push(String(value))
      }
    }
  })

  viewProperties.footerText?.forEach((key) => {
    footerLines.push(t(key, { defaultValue: key }))
  })

  return footerLines
}

const dateFormatter = (date: string): string | Error => {
  const value = DateTime.fromISO(date)
  if (!value.isValid) {
    return new Error(value.toString().replace(/ /g, "_").toUpperCase())
  }
  return value.toFormat("yyyy-MM-dd HH:mm:ss")
}

const timeFormatter = (durationValue: any): string | Error => {
  try {
    return Duration.fromDurationLike({ hours: durationValue }).toFormat("hh:mm")
  } catch (_e) {
    return new Error("INVALID_DURATION")
  }
}

const getData = (
  value: string | number | Error,
  viewProperties: SingleStatViewProperties,
  locale?: string,
): string | Error => {
  if (value instanceof Error) return value

  const decimalPlaces = viewProperties.decimalPlaces ?? 0

  switch (typeof value) {
    case "string":
      return `${viewProperties.prefix} ${value} ${viewProperties.suffix}`.trim()
    case "number":
      return valueFormatter({
        value,
        prefix: viewProperties.prefix,
        suffix: viewProperties.suffix,
        decimalPlaces,
        formatUnits: "formatUnits" in viewProperties ? viewProperties.formatUnits : false,
        useScientificNotation: viewProperties.useScientificNotation,
        locale,
      })
  }
}
